import { useFetch } from '#app';
import { useCookie } from '@/composables/useCookie';
import { useDateTimeManager } from '@/composables/useDateTimeManager';
import { useToastStore } from '@/stores/toast';
import type {
  IDomainSettings,
  IFax,
  IFaxState,
  IFilterFax,
  ISendFax,
  ISetting,
} from '@/types/fax/Types';
import { defineStore } from 'pinia';
import { fixMojibake } from '~/utils/helpers/tsHandlerFunc';

const baseUrl = import.meta.env['VITE_IPBX_URL'];
// @ts-ignore
export const useFaxStore = defineStore('FaxStore', {
  state: (): IFaxState => ({
    faxes: [],
    reports: [],
    pages: 0,
    retrySendFax: '2',
    loading: false,
    error: null,
    receiveInitialState: false,
    setting: {},
  }),
  getters: {},
  actions: {
    async getFaxesList() {
      this.loading = true;
      this.error = null;
      try {
        const { data, error } = await useFetch<IFax[]>('/api/fax/list');
        this.faxes = data.value ?? [];
      } catch (err: any) {
        this.error = err.message;
      } finally {
        this.loading = false;
        this.receiveInitialState = true;
      }
    },
    async getReports(params: IFilterFax) {
      let pageSize = 20;
      this.loading = true;
      const { dateToPersian } = useDateTimeManager();
      try {
        const data = await $fetch<any>('/api/fax/reports', {
          method: 'POST',
          body: {
            ...params,
          },
        });
        this.totalPage = data?.count;
        this.pages = Math.ceil(this.totalPage / pageSize);
        this.reports = data?.items?.map((item: any, index: number) => {
          const fax = this.faxes?.find(
            (fax: any) => fax.FaxUUID == item.FaxUUID
          );
          const dateTime = item.FaxDate.split('T');
          const fileName = item.FaxFile.split('/');
          return {
            ...item,
            row: params.page * pageSize + index + 1,
            index: params.page * 20 + index + 1,
            id: item.FaxLogUUID,
            status: item.FaxStatus,
            date: dateTime[0],
            time: dateTime[1].replace('Z', ''),
            number: item.FaxPeerNumber,
            extension: fax?.FaxExtension,
            file: item.FaxFile,
            fileName: fileName.at(-1),
            direction: item.FaxDirection,
            description: fixMojibake(item.FaxDescription),
          };
        });
      } catch (err: any) {
        console.log(err);
      } finally {
        this.loading = false;
        this.receiveInitialState = true;
      }
    },
    async getSetting() {
      try {
        const { data, error } = await useFetch<IDomainSettings>(
          '/api/fax/setting/list'
        );
        let targetSetting: ISetting[] = [];
        if (data.value?.domain_settings) {
          targetSetting = data.value?.domain_settings;
        } else if (data.value?.default_settings) {
          targetSetting = data.value?.default_settings;
        }
        this.setting =
          targetSetting.find(
            (item: ISetting) => item.subcategory == 'send_retry_limit'
          ) || null;
        this.retrySendFax = this.setting?.value ?? '';
      } catch (err: any) {
        console.log(err);
      }
    },
    async updateSetting(count: string) {
      this.setting.value = count;
      this.retrySendFax = count;
      try {
        const data = await $fetch<any>('/api/fax/setting/update', {
          method: 'POST',
          body: {
            fax_settings: [this.setting],
          },
        });
        return true;
      } catch (err: any) {
        console.log(err);
        return false;
      }
    },
    async sendFax(params: ISendFax): Promise<boolean> {
      const toastStore = useToastStore();
      try {
        if (!params.file || !params.fax_uuid) return false;
        const { getCookie } = useCookie();

        const formData = new FormData();
        formData.append('fax_file', params.file);
        formData.append('fax_uuid', params.fax_uuid);
        formData.append('destination_number', params.destination_number);
        formData.append('description', params.description);
        formData.append('dtmf', params.dtmf);
        const data = await $fetch<IDomainSettings>(`${baseUrl}/fax/send`, {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: `Bearer ${getCookie('accessToken')}`,
          },
        });
        toastStore.showAlert({
          title: 'faxSendSuccessfully',
          showCloseButton: true,
          timerProgressBar: true,
        });
        return true;
      } catch (err: any) {
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        console.log(err);
        return false;
      }
    },
    async insertFax(params: any) {
      const toastStore = useToastStore();
      try {
        const { data, error } = await useFetch<any>('/api/fax/insert', {
          method: 'POST',
          body: params,
        });
        this.faxes?.push({
          FaxUUID: data.value.fax_uuid,
          FaxName: params.name,
          FaxExtension: data.value.fax_extension,
          FaxEmail: params.emails,
        });
        toastStore.showAlert({
          title: 'faxInsertSuccessfully',
          showCloseButton: true,
          timerProgressBar: true,
        });
        return true;
      } catch (err: any) {
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        return false;
      }
    },
    async updateFax(params: any) {
      const toastStore = useToastStore();
      try {
        const { data, error } = await useFetch<any>('/api/fax/update', {
          method: 'PUT',
          body: params,
        });
        const fax = this.faxes.find(
          (item: IFax) => item.FaxUUID == params.fax_uuid
        );
        if (fax) {
          Object.assign(fax, {
            FaxExtension: params.extension,
            FaxEmail: params.emails,
            FaxName: params.name,
          });
        }
        toastStore.showAlert({
          title: 'faxUpdateSuccessfully',
          showCloseButton: true,
          timerProgressBar: true,
        });
        return true;
      } catch (err: any) {
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        return false;
      }
    },
    async removeFax(id: string) {
      const toastStore = useToastStore();
      try {
        const data = await $fetch<any>('/api/fax/remove', {
          method: 'POST',
          body: { id },
        });
        const findIndex = this.faxes.findIndex(
          (item: IFax) => item.FaxUUID == id
        );
        if (findIndex != -1) {
          this.faxes.splice(findIndex, 1);
        }
        toastStore.showAlert({
          title: 'faxDeleteSuccessfully',
          showCloseButton: true,
          timerProgressBar: true,
        });
        return true;
      } catch (err: any) {
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        return false;
      }
    },
    async removeFaxFile(id: string) {
      const toastStore = useToastStore();
      try {
        const data = await $fetch<any>('/api/fax/removeFile', {
          method: 'POST',
          body: { id },
        });
        toastStore.showAlert({
          title: 'faxFileDeleteSuccessfully',
          showCloseButton: true,
          timerProgressBar: true,
        });
        return true;
      } catch (err: any) {
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        return false;
      }
    },
  },

  // persist: {
  //   storage: persistedState.localStorage,
  // },
});
